import { VestingGraphData } from '../../../../../hooks/vesting-task/use-vesting-graph';
import { VESTING_GRAPH_BAR_SLOT_FILL_RATIO } from './vesting-graph.constants';
import {
  calendarTimeOf,
  getTimestampPosition,
  layoutTimestampForDate,
  resolveMilestoneSegmentBounds,
} from './vesting-graph.geometry';
import {
  LayoutChartPoint,
  MilestoneSegmentBound,
  VestingGraphCliffOverlay,
  VestingGraphMilestoneBracket,
} from './vesting-graph.types';

/** Match bar fill so cliff / bracket edges sit on the drawn column, not the full slot. */
const CLIFF_BAR_SLOT_FILL_RATIO = VESTING_GRAPH_BAR_SLOT_FILL_RATIO;

const resolveCliffEndLayoutTimestamp = ({
  chartPoints,
  cliffEndDate,
  domainMin,
}: {
  chartPoints: LayoutChartPoint[];
  cliffEndDate: string;
  domainMin: number;
}) => {
  const cliffEndCalendar = new Date(cliffEndDate).getTime();
  // Only real vesting columns — the empty cliff gutter must not count as a payment.
  const payments = chartPoints.filter(
    (point) => !point.isCliffSpan && (point.periodShares ?? 0) > 0,
  );
  if (!payments.length) return null;

  const exactPayment = payments.find((point) => calendarTimeOf(point) === cliffEndCalendar);
  if (exactPayment) {
    // Stop short of the bar fill centred on that payment.
    return exactPayment.timestamp - exactPayment.slotHalfWidth! * CLIFF_BAR_SLOT_FILL_RATIO;
  }

  const nextIndex = payments.findIndex((point) => calendarTimeOf(point) >= cliffEndCalendar);
  if (nextIndex < 0) {
    const last = payments.at(-1)!;
    return last.timestamp - last.slotHalfWidth! * CLIFF_BAR_SLOT_FILL_RATIO;
  }

  const next = payments[nextIndex]!;
  const rangeEnd = next.timestamp - next.slotHalfWidth! * CLIFF_BAR_SLOT_FILL_RATIO;
  const previous =
    nextIndex === 0
      ? {
          calendar: chartPoints[0] ? calendarTimeOf(chartPoints[0]) : cliffEndCalendar,
          layout: domainMin,
        }
      : {
          calendar: calendarTimeOf(payments[nextIndex - 1]!),
          layout: payments[nextIndex - 1]!.timestamp + payments[nextIndex - 1]!.slotHalfWidth!,
        };
  const nextCalendar = calendarTimeOf(next);
  if (nextCalendar <= previous.calendar) return rangeEnd;

  const progress = Math.min(
    1,
    Math.max(0, (cliffEndCalendar - previous.calendar) / (nextCalendar - previous.calendar)),
  );

  return previous.layout + progress * (rangeEnd - previous.layout);
};

/**
 * Spans the months the cliff withholds. Monthly charts pass `chartPoints` so the bracket
 * uses equal-column x (with calendar interpolation when cliff end is not a payment).
 */
export const buildCliffOverlay = ({
  chartPoints,
  domainMax,
  domainMin,
  pointCount,
  vestingGraph,
}: {
  chartPoints?: LayoutChartPoint[];
  domainMax: number;
  domainMin: number;
  pointCount: number;
  vestingGraph: VestingGraphData;
}): null | VestingGraphCliffOverlay => {
  if (!vestingGraph.cliff) return null;

  // Cliff opens at the left edge of the empty grant-start gutter — never at the gutter's
  // centre, or a wide cliff would look like it starts mid-span.
  const cliffStartLayout =
    chartPoints?.[0] != null
      ? chartPoints[0].timestamp - (chartPoints[0].slotHalfWidth ?? 0)
      : layoutTimestampForDate(chartPoints, vestingGraph.cliff.startDate);

  const leftPercent = getTimestampPosition({
    maxTimestamp: domainMax,
    minTimestamp: domainMin,
    timestamp: cliffStartLayout,
  });

  const endLayoutTimestamp = chartPoints?.length
    ? resolveCliffEndLayoutTimestamp({
        chartPoints,
        cliffEndDate: vestingGraph.cliff.endDate,
        domainMin,
      })
    : null;

  const endPercent =
    endLayoutTimestamp != null
      ? getTimestampPosition({
          maxTimestamp: domainMax,
          minTimestamp: domainMin,
          timestamp: endLayoutTimestamp,
        })
      : getTimestampPosition({
          maxTimestamp: domainMax,
          minTimestamp: domainMin,
          timestamp: new Date(vestingGraph.cliff.endDate).getTime(),
        }) -
        (CLIFF_BAR_SLOT_FILL_RATIO / 2 + 0.04) * (100 / Math.max(pointCount, 1));

  const widthPercent = endPercent - leftPercent;

  if (widthPercent <= 0) return null;

  return {
    cumulativePercentAtCliff: vestingGraph.cliff.cumulativePercentAtCliff,
    leftPercent,
    widthPercent,
  };
};

/** One bracket per milestone — uses the same column bounds as the area segments. */
export const buildMilestoneBrackets = ({
  domainMax,
  domainMin,
  segmentBounds,
  vestingGraph,
}: {
  domainMax: number;
  domainMin: number;
  segmentBounds?: MilestoneSegmentBound[];
  vestingGraph: VestingGraphData;
}): VestingGraphMilestoneBracket[] => {
  const bounds =
    segmentBounds?.length === vestingGraph.points.length
      ? segmentBounds
      : resolveMilestoneSegmentBounds({
          domainMax,
          domainMin,
          points: vestingGraph.points,
        });

  return vestingGraph.points.map((point, index) => {
    const segment = bounds[index]!;
    const leftPercent = getTimestampPosition({
      maxTimestamp: domainMax,
      minTimestamp: domainMin,
      timestamp: segment.startTimestamp,
    });
    const endPercent = getTimestampPosition({
      maxTimestamp: domainMax,
      minTimestamp: domainMin,
      timestamp: segment.endTimestamp,
    });

    return {
      description: point.milestoneName ?? '',
      label: `M${index + 1}`,
      leftPercent,
      widthPercent: Math.max(endPercent - leftPercent, 0),
      yPercent: point.cumulativePercent,
    };
  });
};
